import { cloneDeep } from 'lodash-es';
import { BetDateType, Currency } from '@/constants';
import { todayDateRangeInUserTimeZone } from '@/helpers';
import { applyDatePeriodPresetToBetDate } from '@/helpers/applyDatePeriodPresetToBetDate';
import { UserBalanceItem, UserBalanceStats } from '@/models/userBalance';
import { createUserBalancePageHeaders } from '@/pages/accounting/userBalance/UserBalancePageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { userBalanceService } from '@/services/userBalanceService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { useUserStore } from '@/stores/user';
import { IPageOptions } from '@/types/page';
import { IUserBalanceFilters, IUserBalanceStatsApi } from '@/types/userBalance';
import type { DataTableHeader } from '@/types/vuetify';

const PRIMARY_STORE_ID = 'userBalance';

const defaultStatsApi: IUserBalanceStatsApi = {};

function normalizeStats(stats: IUserBalanceStatsApi | IUserBalanceStatsApi[] | undefined | null): UserBalanceStats {
    if (Array.isArray(stats)) {
        return new UserBalanceStats(stats[0] ?? defaultStatsApi);
    }
    return new UserBalanceStats(stats ?? defaultStatsApi);
}

function createUserBalanceStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<UserBalanceItem[]>([]);
    const stats = shallowRef<UserBalanceStats>(new UserBalanceStats(defaultStatsApi));
    const total = ref(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();
    const isDetail = storeId !== PRIMARY_STORE_ID;

    // --- filters ---
    const filters = reactive<IUserBalanceFilters>({
        betDateType: {
            value: BetDateType.PlacementLondon,
            title: 'bets.betDateType',
            allowSaveState: true,
            disableClear: true,
        },
        datePeriod: {
            value: 'range',
            title: 'bookBalance.datePeriod.title',
            allowSaveState: true,
            disableClear: true,
        },
        betDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'bookBalance.betDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        betBookmakerId: {
            value: null,
            title: 'bets.bookmaker',
            allowSaveState: true,
        },
        betTradingTypeId: {
            value: null,
            title: 'bets.tradingType',
            allowSaveState: true,
        },
        betEventPeriodId: {
            value: null,
            title: 'bets.periodType',
            allowSaveState: true,
        },
        betEventType: {
            value: null,
            title: 'common.marketType',
            allowSaveState: true,
        },
        playerCurrencyId: {
            value: Currency.Euro,
            title: 'common.playerCurrency',
            allowSaveState: true,
            disableClear: true,
        },
        userObserveId: {
            value: null,
            title: 'bets.userObserveId',
            allowSaveState: true,
        },
        userAgentId: {
            value: null,
            title: 'bets.agent',
            allowSaveState: false,
            forApiOnly: true,
        },
        betBetVsOpen: {
            value: null,
            title: 'bets.betVsOpen',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const userStore = useUserStore();
    const filterPanel = useFilterPanelState(filters);
    const availableFilters = computed(() => filterPanel.availableFilters.value.filter((f) => f.key !== 'betBetVsOpen' || userStore.isAdmin()));
    const visibleFilters = computed(() => filterPanel.visibleFilters.value.filter((key) => key !== 'betBetVsOpen' || userStore.isAdmin()));

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [],
    });

    // --- reset ---
    function resetFilters() {
        filters.betDateType.value = BetDateType.PlacementLondon;
        filters.datePeriod.value = 'range';
        filters.betDate.value = todayDateRangeInUserTimeZone();
        filters.betBookmakerId.value = null;
        filters.betTradingTypeId.value = null;
        filters.betEventPeriodId.value = null;
        filters.betEventType.value = null;
        filters.playerCurrencyId.value = Currency.Euro;
        filters.userObserveId.value = null;
        if (!isDetail) {
            filters.userAgentId.value = null;
        }
        filters.betBetVsOpen.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createUserBalancePageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: PRIMARY_STORE_ID,
        filters,
        saveFiltersState: filterPanel.saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersCopy = cloneDeep(filters);
            applyDatePeriodPresetToBetDate(filtersCopy);

            const params = {
                filters: filtersCopy,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: ['Stats'],
                requiredDictionaries: ['Site', 'ToolUserType', 'CurrencyAll', 'Agent', 'Player'],
                requestKey: `user-balance:${storeId}`,
            };

            const response = isDetail ? await userBalanceService.detail(params) : await userBalanceService.list(params);
            items.value = (response.data.rows ?? []).map((row) => new UserBalanceItem(row));
            stats.value = normalizeStats(response.data.stats);
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        stats.value = new UserBalanceStats(defaultStatsApi);
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        isDetail,
        items,
        stats,
        total,
        filters,
        hiddenFilters: filterPanel.hiddenFilters,
        visibleFiltersMobile: filterPanel.visibleFiltersMobile,
        filterOrder: filterPanel.filterOrder,
        availableFilters,
        useButtonForFiltersApply: filterPanel.useButtonForFiltersApply,
        saveFiltersState: filterPanel.saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        hasRefresh: true,
        hasExport: false,
    };
}

export function useUserBalanceStore(storeId: string) {
    const useStore = defineStore(storeId, () => createUserBalanceStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, PRIMARY_STORE_ID),
    });
    return useStore();
}

export type UserBalanceStoreSetup = ReturnType<typeof useUserBalanceStore>;
